import React, { useMemo } from 'react';

import { useItemContext } from 'components/SingleItem/ItemContext';
import OptionList from 'components/SingleItem/OptionGroups/OptionList';
import { formatTextToPriceInt } from 'utils';
import TitleSwitch from './TitleSwitch';
import styles from './AppPriceInput.module.scss';
import { getAppPrices } from './utils';
import { Venue } from '../../generated/graphql';

interface Props {
  handleSetSamePriceOnAllApps: () => void;
  isSameOnAllApps: boolean;
  currentVenue: Venue;
  hasBrands: boolean;
}

const AppPriceInput: React.FC<Props> = ({ handleSetSamePriceOnAllApps, isSameOnAllApps, currentVenue, hasBrands }) => {
  const { item, handleChange: handleParentChange, posProduct } = useItemContext();

  const initPosPrice = posProduct ? posProduct.unitPrice : null;

  const itemHasSurcharge = useMemo(() => {
    return (
      currentVenue?.menus?.some((menu) => {
        if (!menu.surcharge) {
          return false;
        }

        if (menu.surcharge.amount === 0) {
          return false;
        }

        if (!menu.products) {
          return false;
        }

        return menu.products.some((product) => product.mmId === item?.mmId);
      }) ?? false
    );
  }, [currentVenue, item?.mmId]);

  if (!item) {
    return null;
  }

  const rows = getAppPrices(item, isSameOnAllApps, initPosPrice, currentVenue.name, hasBrands, itemHasSurcharge);
  const prices = rows.filter((_, index) => index !== rows.length - 1).map((row) => row.prices[0].prices[0]);
  const areAllPricesSame = prices.every((price) => price === prices[0]);

  const handleChange = (index: number, _: string, value: string): void => {
    const newPrice = formatTextToPriceInt(value);

    if (!isSameOnAllApps) {
      const appId = rows[index]?.apps?.[0]?.id;
      handleParentChange({
        key: 'variablesByApp',
        payload: item.variablesByApp.map((value) => (value.app.id === appId ? { ...value, price: newPrice } : value))
      });
    } else {
      handleParentChange({
        key: 'variablesByApp',
        payload: item.variablesByApp.map((value) => ({ ...value, price: newPrice }))
      });
    }
  };

  return (
    <div className={styles.Outer}>
      {areAllPricesSame && (
        <TitleSwitch title="Same price on all apps" checked={isSameOnAllApps} onChange={handleSetSamePriceOnAllApps} />
      )}
      <OptionList
        handleChange={handleChange}
        parentId={item.posId || ''}
        options={rows}
        currentVenue={currentVenue}
        posPrice={posProduct?.unitPrice}
      />
    </div>
  );
};

export default AppPriceInput;
